import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { blastElementId, type BlastRadiusScoreDocument } from '../../api-utils/blast';
import type { BusinessElementDocument, ThreatElementDocument } from '../../api-utils/elements';
import type { ForceGraphNode } from './correlationGraph';
import EbrWalkTree, { EBR_LAYER_COLORS } from './EbrWalkTree';
import {
  assembleEbrWalkHierarchy,
  ebrTreeRows,
  ensureCrownJewelNode,
  extractCrownPathHighlight,
  layerFromQualifiedId,
  parseNodeRef,
  qualifyNodeId,
  unwrapHandlerRecord,
  type EbrWalkHierarchyNode,
  type EbrWalkLayer,
} from './ebrTreeGraph';
import { ebrScoreOf, titleCase } from './dossierModel';
import { formatClock, scoreCls, scoreColor } from './eriUtils';
import { useResizableRail } from './useResizableRail';

function countLayers(root: EbrWalkHierarchyNode | null): Record<EbrWalkLayer, number> {
  const counts: Record<EbrWalkLayer, number> = { threat: 0, infrastructure: 0, business: 0 };
  const seen = new Set<string>();
  const walk = (node: EbrWalkHierarchyNode) => {
    if (!seen.has(node.id)) {
      seen.add(node.id);
      counts[node.layer] += 1;
    }
    node.children?.forEach(walk);
  };
  if (root) walk(root);
  return counts;
}

interface EffectiveBlastRadiusMapProps {
  score: BlastRadiusScoreDocument;
  inventory: ForceGraphNode[];
  business?: BusinessElementDocument[];
  threats?: ThreatElementDocument[];
  /** Live run_forward payload overlays persisted crown path after Recalculate. */
  liveWalk?: unknown;
  onBack: () => void;
  onOpenEntity?: (id: string, name: string) => void;
  backLabel?: string;
  hideHeaderMeta?: boolean;
  /** Rendered inside the header card, above the walk toolbar and legend. */
  header?: React.ReactNode;
}

function buildLabelMap(
  inventory: ForceGraphNode[],
  business: BusinessElementDocument[],
  threats: ThreatElementDocument[],
  score: BlastRadiusScoreDocument
): Map<string, string> {
  const labels = new Map<string, string>();
  const put = (bare: string, ring: string, name: string) => {
    if (!bare || !name) return;
    labels.set(bare, name);
    labels.set(`${ring}/${bare}`, name);
  };
  for (const node of inventory) {
    put(node.id, 'infrastructure_elements', node.label || node.name);
  }
  for (const doc of business) {
    const id = String(doc._id || '').trim();
    put(id, 'business_elements', String(doc.name || id));
  }
  for (const doc of threats) {
    const id = String(doc._id || '').trim();
    put(id, 'threat_elements', String(doc.name || id));
  }
  const start = blastElementId(score);
  if (start && score.element_name) {
    put(start, 'infrastructure_elements', String(score.element_name));
  }
  const jewel = score.crown_jewel;
  if (jewel?.node_id && jewel.name) {
    const { id, ring } = parseNodeRef(qualifyNodeId(jewel.node_id));
    put(id, ring || 'business_elements', String(jewel.name));
  }
  const putLayer = (raw: unknown, ring: string) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
    const items = (raw as { items?: Array<{ id?: string; name?: string }> }).items;
    if (!Array.isArray(items)) return;
    for (const item of items) {
      const qualified = qualifyNodeId(String(item.id || ''), ring);
      if (!qualified || !item.name) continue;
      const { id } = parseNodeRef(qualified);
      put(id, ring, String(item.name));
    }
  };
  putLayer(score.reachable_business_elements, 'business_elements');
  putLayer(score.reachable_adversarial_elements, 'threat_elements');
  return labels;
}

const EffectiveBlastRadiusMap: React.FC<EffectiveBlastRadiusMapProps> = ({
  score,
  inventory,
  business = [],
  threats = [],
  liveWalk,
  onBack,
  onOpenEntity,
  backLabel = 'Back to EBR list',
  hideHeaderMeta = false,
  header,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdversarial, setShowAdversarial] = useState(false);
  const [showUnconfirmed, setShowUnconfirmed] = useState(false);
  const tree = ebrTreeRows(score);
  const ebr = ebrScoreOf(score) ?? 0;
  const startId = blastElementId(score);
  const labels = useMemo(
    () => buildLabelMap(inventory, business, threats, score),
    [inventory, business, threats, score]
  );

  const hierarchy = useMemo(() => {
    const root = assembleEbrWalkHierarchy(score, labels, {
      showAdversarial,
      showUnconfirmed,
      business,
      threats,
    });
    if (!root) return null;
    const live = unwrapHandlerRecord(liveWalk) || score;
    const { segments, crownId } = extractCrownPathHighlight(live);
    ensureCrownJewelNode(root, crownId, segments, labels);
    return root;
  }, [score, labels, business, threats, liveWalk, showAdversarial, showUnconfirmed]);

  const crown = useMemo(() => {
    const live = unwrapHandlerRecord(liveWalk) || score;
    return extractCrownPathHighlight(live);
  }, [liveWalk, score]);

  // Replay walk intro after Recalculate (liveWalk is a new payload each run).
  const [introKey, setIntroKey] = useState(0);
  const prevLiveWalkRef = useRef(liveWalk);
  useEffect(() => {
    if (liveWalk && liveWalk !== prevLiveWalkRef.current) {
      prevLiveWalkRef.current = liveWalk;
      setIntroKey((n) => n + 1);
    }
  }, [liveWalk]);

  const rail = useResizableRail('c3-ebr-rail-width');
  const hasTree = tree.length > 0;
  const labelFor = (id: string) => labels.get(id) || labels.get(parseNodeRef(id).id) || parseNodeRef(id).id;
  const selectedLabel = selectedId ? labelFor(selectedId) : '';
  const selectedBare = selectedId ? parseNodeRef(selectedId).id : '';
  const selectedNodeId = selectedId || qualifyNodeId(`infrastructure_elements/${startId}`);

  const layerCounts = useMemo(() => countLayers(hierarchy), [hierarchy]);

  const crownHops = useMemo<CrownHop[]>(() => {
    const ids: string[] = [];
    for (const seg of crown.segments) {
      if (ids.length === 0) ids.push(seg.from);
      if (ids[ids.length - 1] !== seg.from) ids.push(seg.from);
      ids.push(seg.to);
    }
    return ids.map((id) => ({
      id,
      label: labelFor(id),
      layer: layerFromQualifiedId(id),
      isJewel: Boolean(crown.crownId && id === crown.crownId),
    }));
    // labelFor only reads `labels`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crown, labels]);

  return (
    <div id="c3EbrMap" className="space-y-3">
      {!hideHeaderMeta ? (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="c3-pill" onClick={onBack}>
            <ArrowLeft className="w-3.5 h-3.5" />
            {backLabel}
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-semibold truncate">
              {score.element_name || startId || 'Element'}
            </div>
            <div className="text-[12px] text-c3-mute">
              {[
                score.band,
                score.max_domain ? `max ${titleCase(String(score.max_domain))}` : '',
                `reach ${Number(score.reachability_count || tree.length || 0).toLocaleString('en-US')}`,
                score.truncated === true || String(score.truncated) === 'True' ? 'truncated' : '',
                score.computed_at ? `updated ${formatClock(score.computed_at)}` : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[20px] font-bold ${scoreCls(ebr)}`}>{Math.round(ebr)}</span>
            <div className="eri-bar w-24">
              <i style={{ width: `${Math.max(0, Math.min(100, ebr))}%`, background: scoreColor(ebr) }} />
            </div>
          </div>
        </div>
      ) : null}

      {header ? <div className="c3-ebr-head">{header}</div> : null}

      {!hasTree ? (
        <p className="text-c3-mute text-sm m-0">
          No reachability tree on this score yet. Use Recalculate EBR to run{' '}
          <code className="text-c3-lpurple">blast_radius.run_forward</code> and persist{' '}
          <code className="text-c3-lpurple">reachable_infrastructure_elements</code>.
        </p>
      ) : (
        <div
          {...rail.bodyProps}
          className={`c3-graph-body c3-ebr-body${rail.resizing ? ' is-resizing' : ''}`}
        >
          <div className="c3-graph-canvas">
            <div className="c3-ebr-overlay">
              <div className="c3-graph-titlebar">
                <div className="c3-graph-title">Blast radius · walk</div>
              </div>
              <div className="c3-ebr-switches">
                <RailSwitch
                  label="Adversarial"
                  checked={showAdversarial}
                  onChange={setShowAdversarial}
                />
                <RailSwitch
                  label="Unconfirmed"
                  checked={showUnconfirmed}
                  onChange={setShowUnconfirmed}
                />
              </div>
              {selectedBare && onOpenEntity ? (
                <button
                  type="button"
                  className="c3-ebr-open"
                  onClick={() => onOpenEntity(selectedBare, selectedLabel || selectedBare)}
                  title={`Open ${selectedLabel || selectedBare} in the Entity Graph`}
                >
                  View in graph
                </button>
              ) : null}
            </div>
            <EbrWalkTree
              data={hierarchy}
              selectedNodeId={selectedNodeId}
              onSelectNode={(id) => setSelectedId(id)}
              crownPathSegments={crown.segments}
              crownJewelId={crown.crownId}
              introKey={introKey}
              className="h-full w-full"
            />
          </div>
          {rail.splitter}
          <aside className="c3-attack-rail">
            <div className="c3-attack-path">
              <div className="c3-attack-k">Path to the crown jewel</div>
              <CrownPathSteps
                hops={crownHops}
                selectedId={selectedNodeId}
                onSelect={setSelectedId}
              />
            </div>
            <div className="c3-attack-legend">
              <EbrRenderedLayers
                counts={layerCounts}
                showUnconfirmed={showUnconfirmed}
                reach={Number(score.reachability_count || tree.length || 0)}
                maxDomain={score.max_domain ? titleCase(String(score.max_domain)) : ''}
              />
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

interface CrownHop {
  id: string;
  label: string;
  layer: EbrWalkLayer;
  isJewel: boolean;
}

function RailSwitch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="c3-domain-color-switch">
      <span>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={`c3-toggle${checked ? ' is-on' : ''}`}
        onClick={() => onChange(!checked)}
      >
        <span className="c3-toggle-knob" />
      </button>
    </label>
  );
}

function LayerSwatch({ layer, size = 9 }: { layer: EbrWalkLayer; size?: number }) {
  if (layer === 'threat') {
    return (
      <span
        className="c3-legend-diamond"
        style={{ width: size, height: size, backgroundColor: EBR_LAYER_COLORS.threat, borderColor: EBR_LAYER_COLORS.threat }}
        aria-hidden
      />
    );
  }
  return (
    <span
      className={layer === 'business' ? 'c3-legend-square' : 'c3-legend-circle'}
      style={{ backgroundColor: EBR_LAYER_COLORS[layer] }}
      aria-hidden
    />
  );
}

function CrownPathSteps({
  hops,
  selectedId,
  onSelect,
}: {
  hops: CrownHop[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  if (hops.length === 0) {
    return <p className="c3-attack-empty">No path to a crown jewel on this walk.</p>;
  }
  return (
    <ol className="c3-stepper">
      {hops.map((hop, index) => (
        <li
          key={hop.id}
          className={`c3-stepper-row c3-crown-step${hop.id === selectedId ? ' is-selected' : ''}`}
        >
          <span className={`c3-stepper-idx${hop.isJewel ? ' is-jewel' : ''}`}>{index + 1}</span>
          <button type="button" className="c3-crown-step-btn" onClick={() => onSelect(hop.id)}>
            <span className="c3-stepper-title">
              <LayerSwatch layer={hop.layer} />
              <span className="c3-crown-step-name">{hop.label}</span>
            </span>
            <span className="c3-stepper-time">
              {hop.isJewel ? 'crown jewel' : index === 0 ? 'blast origin' : titleCase(hop.layer)}
            </span>
          </button>
        </li>
      ))}
    </ol>
  );
}

function EbrRenderedLayers({
  counts,
  showUnconfirmed,
  reach,
  maxDomain,
}: {
  counts: Record<EbrWalkLayer, number>;
  showUnconfirmed: boolean;
  reach: number;
  maxDomain: string;
}) {
  const rows: Array<{ layer: EbrWalkLayer; label: string }> = [
    { layer: 'threat', label: 'Threat' },
    { layer: 'infrastructure', label: 'Infrastructure' },
    { layer: 'business', label: 'Business' },
  ];
  return (
    <div className="c3-entity-legend">
      <div className="c3-entity-legend-k">Rendered layers</div>
      <div className="c3-entity-legend-rows">
        {rows.map((row) => (
          <div key={row.layer} className="c3-entity-legend-row">
            <span className="c3-entity-legend-swatch">
              <LayerSwatch layer={row.layer} />
            </span>
            <span className="c3-entity-legend-label">{row.label}</span>
            <span className="c3-entity-legend-count">{counts[row.layer]}</span>
          </div>
        ))}
      </div>
      <div className="c3-entity-legend-k">Walk key</div>
      <div className="c3-entity-legend-rows">
        <div className="c3-entity-legend-row">
          <span className="c3-entity-legend-swatch">
            <span className="c3-legend-line" style={{ backgroundColor: EBR_LAYER_COLORS.crown }} aria-hidden />
          </span>
          <span className="c3-entity-legend-label">path to crown jewel</span>
        </div>
        {showUnconfirmed ? (
          <div className="c3-entity-legend-row">
            <span className="c3-entity-legend-swatch">
              <span className="c3-legend-line is-dashed" aria-hidden />
            </span>
            <span className="c3-entity-legend-label">unconfirmed match</span>
          </div>
        ) : null}
      </div>
      <div className="c3-entity-legend-foot">
        <div>
          reach {reach.toLocaleString('en-US')}
          {maxDomain ? ` · max ${maxDomain}` : ''}
        </div>
      </div>
    </div>
  );
}

export default EffectiveBlastRadiusMap;
